/* eslint-disable default-case */
import React, { createContext, useEffect, useReducer } from 'react'
import { cognitoConfig } from 'config'
import { MatxLoading } from 'app/components'
import { Auth, Hub } from 'aws-amplify'
import { toast } from 'react-toastify'
import { clearStore } from 'app/store'

Auth.configure(cognitoConfig)

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'COGNITO_AUTH_STATE_CHANGED': {
      const { isAuthenticated, user } = action.payload

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
      }
    }
    default: {
      return { ...state }
    }
  }
}

const AuthContext = createContext({
  ...initialAuthState,
  method: 'COGNITO',
  signUp: () => Promise.resolve(),
  signInWithEmailAndPassword: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  forgotPassword: () => Promise.resolve(),
  forgotPasswordSubmit: () => Promise.resolve(),
  changePassword: () => Promise.resolve(),
  singOutGlobal: () => Promise.resolve(),
})

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState)

  const signInWithEmailAndPassword = async (email, password) => {
    return await Auth.signIn(email, password)
  }

  const logout = async () => {
    clearStore()
    return await Auth.signOut()
  }

  const createUserWithEmailAndPassword = async (data) => {
    await Auth.signUp(data)
    toast.success(
      'User created successfully, Check you email to confirm your account.'
    )
  }

  const forgotPassword = async (email) => {
    return await Auth.forgotPassword(email)
  }
  const forgotPasswordSubmit = async (email, code, password) => {
    return await Auth.forgotPasswordSubmit(email, code, password)
  }
  const changePassword = async (old_password, new_password) => {
    const user = await Auth.currentAuthenticatedUser()
    return await Auth.changePassword(user, old_password, new_password)
  }
  const updateUserAttributes = async (new_attributes) => {
    const user = await Auth.currentAuthenticatedUser()
    return await Auth.updateUserAttributes(user, new_attributes)
  }
  const singOutGlobal = async () => {
    try {
      toast.warn('Signing out from all devices...')
      await Auth.signOut({ global: true })
      toast.success('Signed out from all devices')
    } catch (error) {
      toast.error('Error signing out from all devices')
      throw error;
    }
  }

  const setAuthState = (payload) => {
    dispatch({
      type: 'COGNITO_AUTH_STATE_CHANGED',
      payload: payload,
    })
  }

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((user) =>
        setAuthState({
          isAuthenticated: true,
          user: user.attributes,
        })
      )
      .catch(() =>
        setAuthState({
          isAuthenticated: false,
          user: null,
        })
      )
    Hub.listen('auth', (data) => {
      switch (data.payload.event) {
        case 'signIn':
          Auth.currentAuthenticatedUser().then((user) => {
            setAuthState({
              isAuthenticated: !!user,
              user: user ? user.attributes : null,
            })
          })
          return setAuthState({
            isAuthenticated: true,
            user: {},
          })
        case 'signOut':
          return setAuthState({
            isAuthenticated: false,
            user: null,
          })
        case 'tokenRefresh':
          Auth.currentUserInfo().then((user) => {
            return setAuthState({
              isAuthenticated: true,
              user: user?.attributes,
            })
          })
          return
        case 'tokenRefresh_failure':
          if (data.payload.data?.code === 'NotAuthorizedException') {
            logout()
          }
      }
    })

    return () => Hub.remove('auth')
  }, [])

  if (!state.isInitialised) {
    return <MatxLoading />
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'COGNITO',
        signUp: createUserWithEmailAndPassword,
        signInWithEmailAndPassword,
        logout,
        forgotPassword,
        forgotPasswordSubmit,
        changePassword,
        updateUserAttributes,
        singOutGlobal,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
